import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { GlobalsStore } from '../../../stores/globals.state';
import { Button } from 'primeng/button';
import { FeatureProperties } from '../../../models/geo-data';
import { TooltipModule } from 'primeng/tooltip';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { DataLayer } from '../../../models/data-layer';
import { GridStore } from '../../../stores/grid/grid.state';
import { WidgetStore } from '../../../stores/grid/widget.state';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { DataLayerType } from '../../../models/data-layer-type';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-table-row-district]',
  standalone: true,
  imports: [Button, TooltipModule, SvgIconComponent],
  templateUrl: './table-row-district.component.html',
  styleUrl: './table-row-district.component.scss',
})
export class TableRowDistrictComponent implements OnInit {
  readonly globalStore = inject(GlobalsStore);
  readonly gridStore = inject(GridStore);
  readonly widgetStore = inject(WidgetStore);
  readonly dataLayerStore = inject(DataLayerStore);
  readonly dataLayerTypeStore = inject(DataLayerTypeStore);
  readonly dataStore = inject(IndicatorDataStore);
  @Input() item!: FeatureProperties;
  @Output() closeEvent = new EventEmitter<void>();
  @Output() generateViewEvent = new EventEmitter<DataLayerType>();

  icon = '';

  ngOnInit() {
    this.icon = this.item.isPoi ? 'fas fa-location-dot' : 'fas fa-thumb-tack';
  }

  getParent(feature: FeatureProperties): DataLayerType | undefined {
    const typeId = this.getTypeId(feature);
    return typeId
      ? this.dataLayerTypeStore.getById(typeId) || undefined
      : undefined;
  }

  getTypeId(feature: FeatureProperties) {
    return this.dataLayerStore.getParentIdByGeoDataId(feature['geoData']);
  }

  /**
   * Generates and emits a view event for the given data layer type, then closes the parent component.
   *
   * @param {FeatureProperties} feature - The data layer type for which the view event should be generated.
   * @return {void} This method does not return a value.
   */
  generateView(feature: FeatureProperties): void {
    if (feature && feature['geoData']) {
      const typeId = this.dataLayerStore.getParentIdByGeoDataId(
        feature['geoData']
      );
      const type = typeId ? this.dataLayerTypeStore.getById(typeId) : undefined;

      if (type) this.generateViewEvent.emit(type);
    } else {
      console.log('type not found', feature);
    }
  }

  /**
   * Closes the parent component by emitting a close event.
   *
   * @return {void} No return value.
   */
  closeParent(): void {
    this.closeEvent.emit();
  }

  /**
   * Marks or unmarks a feature in the atlas based on its presence in the selected features list.
   *
   * @param {string} key - key of district to mark
   * @return {void} This method does not return anything.
   */
  markInAtlas(key: string): void {
    setTimeout(() => {
      this.globalStore.triggerAtlasEvent('markDistrict', { key });
      this.closeParent();
    }, 100);
  }

  /**
   * Opens the specified item in the Atlas view.
   *
   * @param {FeatureProperties} item - The feature properties object containing details such as geographic data and keys.
   * @return {void} This method does not return a value.
   */
  openInAtlas(item: FeatureProperties): void {
    const layer: DataLayer | undefined = this.dataLayerStore
      .getLayersByGeoData(item['geoData'])
      ?.at(0);

    if (!layer) return;
    this.globalStore.setActiveReport(layer._id, item.key);
    this.closeParent();
  }

  /**
   * Determines and returns the appropriate icon class for a specific item based on its selection status.
   *
   * @param {string} key - The unique identifier of the item to check.
   * @return {string} The icon class name, either 'fas fa-thumbtack-slash' if the item is selected, or 'fas fa-thumbtack' if it is not.
   */
  getTackIcon(key: string) {
    return this.gridStore
      .options()
      ?.selectedFeatures?.some(item => item.key === String(key))
      ? 'fas fa-thumbtack-slash'
      : 'fas fa-thumbtack';
  }
}
